import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import LoadingOverlay from "react-loading-overlay";
import axios from "axios";
import { CheckInOutContext } from "../CheckInOutContext";
import StyledTable from "./StyledTable";
import LabeledTextField from "./LabeledTextField"
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddIcon from '@mui/icons-material/Add';
import { UserContext } from '../UserContext';
import DrawerComponent from "./common/DrawerComponent";


export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebars, setSidebars] = useState([]);
  const [roleId, setRoleId] = useState(null)
  const { user_id, role } = useContext(UserContext);


  // If you use context for date, keep this, otherwise remove
  const { selectedDate, setSelectedDate } = useContext(CheckInOutContext) || {};

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get("/api/employees");
        
        // --- Process the data to handle null values ---
        const processedData = response.data.map(employee => ({
          ...employee,
          // If total_count is null or undefined, it will be 0. Otherwise, it keeps its original value.
          total_count: employee.total_count ?? 0,
          // It's good practice to do this for win_count as well, in case it can also be null.
          win_count: employee.win_count ?? 0,
        }));

        setEmployees(processedData); // Set the processed data
        setLoading(false);
        setOverlayVisible(false);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setLoading(false);
        setOverlayVisible(false);
      }
    };
    fetchEmployees();
  }, []);


  useEffect(() => {
    async function fetchRoleId() {
      if (!role) return;
      try {
        const response = await axios.get(
          `/api/get-role-id/`,
          { params: { role_name: role } }
        );
        setRoleId(response.data.role_id);
      } catch (error) {
        console.error("Error fetching role id:", error);
        toast.error("Failed to fetch role id");
      }
    }
    fetchRoleId();
  }, [role]);


  useEffect(() => {
    async function fetchSidebarPermissions() {
      if (!roleId) return;
      setIsLoading(true);
      try {
        const response = await axios.get(
          `/api/sidebars/role/${roleId}/`
        );
        setSidebars(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching sidebar permissions:", error);
        toast.error("Failed to fetch sidebar permissions");
      } finally {
        setIsLoading(false);
      }
    }
    fetchSidebarPermissions();
  }, [roleId]);

  console.log(sidebars, "11111111111");

  const addProductSidebar = sidebars.find(
    (item) => item.action_link === "/UserRegistration"
  );

  const rolePermissions = addProductSidebar?.roles.find(
    (role) => role.role_id === roleId
  )?.permissions;


  // --- Table columns for StyledTable ---
  const columns = [
    { field: "first_name", headerName: "First Name" },
    { field: "last_name", headerName: "Last Name" },
    { field: "username", headerName: "User Name" },
    { field: "total_count", headerName: "Total Count" },
    { field: "win_count", headerName: "Win Count" },
    { field: "role", headerName: "Role" },
    { field: "password", headerName: "Password" },
    { field: "actions", headerName: "Actions", align: "center" },
  ];

  // --- Edit Handler ---
  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setOpenDialog(true);
  };

  // --- Delete Handlers ---
  const handleDelete = (employeeId) => {
    setEmployeeToDelete(employeeId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(
        `api/employee/delete/${employeeToDelete}/`
      );
      setEmployees((prev) =>
        prev.filter((employee) => employee.id !== employeeToDelete)
      );
      setOpenDeleteDialog(false);
      toast.success("Employee deleted successfully");
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to delete employee");
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  // --- Update Handler ---
  const handleUpdate = async () => {
    if (!selectedEmployee) return;
    try {
      const response = await axios.put(
        `api/employee/update?id=${selectedEmployee.id}`,
        selectedEmployee
      );
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === selectedEmployee.id ? response.data : emp
        )
      );
      setOpenDialog(false);
      toast.success("Employee Updated Successfully.")
    } catch (error) {
      console.error("Error updating employee:", error);
      toast.error("Error Adding Employee")
    }
  };

  const handleAdd = async () => {
    if (!selectedEmployee) return;
    try {
      const response = await axios.post(
        'api/employee/create/',
        selectedEmployee
      );
      toast.success("Employee Added Successfully.")
      setEmployees((prev) => [...prev, response.data]);
      setOpenAddDialog(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error("Error adding employee:", error);
      // toast.error("Error Adding employee.")
    }
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedEmployee(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const [currentDate, setCurrentDate] = useState(getFormattedDate());

  function getFormattedDate() {
    const now = new Date();
    const day = now.toLocaleString("en-US", { weekday: "short" });
    const month = now.toLocaleString("en-US", { month: "short" });
    const date = now.toLocaleString("en-US", { day: "2-digit" });
    const year = now.getFullYear();
    const time = now.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${day}, ${month} ${date}, ${year} ${time}`;
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(getFormattedDate());
    }, 1000);
    return () => clearInterval(timer);
  }, []);


  // --- Render ---
  return (
    <div style={{ position: "relative" }}>
      <LoadingOverlay
        active={overlayVisible}
        spinner
        text="Loading..."
        styles={{
          wrapper: {
            position: overlayVisible ? "fixed" : "none",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            zIndex: 9999,
            display: overlayVisible ? "block" : "none",
          },
        }}
      />
      <ToastContainer />
      <div style={{ padding: '20px', display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <Box style={{ display: "flex", alignItems: "center" }}>
          <SummarizeIcon style={{ color: "#fb7102", fontSize: "28px" }} />
          <span className="Title">Employee List</span>
        </Box>
        <span className="Title" style={{ marginLeft: "auto", fontWeight: "bold" }}>{currentDate}</span>
      </div>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            bgcolor: "transparent",
            boxShadow: "none",
          }}
        >
          <Typography variant="h4" sx={{ textAlign: "left", marginLeft: "5%" }}>
            All Employees
          </Typography>
          <button
            onClick={() => {
              if (rolePermissions?.is_add) {
                setSelectedEmployee({
                  first_name: "",
                  last_name: "",
                  username: "",
                  password: "",
                  role: "",
                });
                setOpenAddDialog(true);
              } else {
                toast.error("You don't have permission to add User.");
              }
            }}

            className="button_style_employee">
            <AddIcon style={{ verticalAlign: 'middle', marginRight: 6, fontSize: 25 }} />
            Add Employee
          </button>
        </Box>


        <StyledTable
          columns={columns}
          data={employees}
          onEdit={handleEdit}
          onDelete={handleDelete}
          itemsPerPage ={4}
        />
      </Box>

      <DrawerComponent
        open={openDialog || openAddDialog}
        onClose={() => {
          setOpenDialog(false);
          setOpenAddDialog(false);
          setSelectedEmployee(null);
        }}
        title={openDialog ? "Edit Employee" : "Add Employee"}
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            openDialog ? handleUpdate() : handleAdd();
          }}
        >
          <LabeledTextField
            label="First Name"
            value={selectedEmployee?.first_name ?? ""}
            onChange={e =>
              setSelectedEmployee({
                ...selectedEmployee,
                first_name: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
          />
          <LabeledTextField
            label="Last Name"
            value={selectedEmployee?.last_name ?? ""}
            onChange={e =>
              setSelectedEmployee({
                ...selectedEmployee,
                last_name: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
          />
          <LabeledTextField
            label="User Name"
            value={selectedEmployee?.username ?? ""}
            onChange={e =>
              setSelectedEmployee({
                ...selectedEmployee,
                username: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
          />
          <LabeledTextField
            label="Password"
            value={selectedEmployee?.password ?? ""}
            onChange={e =>
              setSelectedEmployee({
                ...selectedEmployee,
                password: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
          />
          <label style={{ color: 'grey', fontWeight: 'bold', mb: 0.5, borderRadius: 2 }}>Role</label>
          <FormControl fullWidth margin="normal" required sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: "2px solid #fb7102", borderRadius: 2 }, '&:hover fieldset': { borderColor: '#fb7102', }, '&.Mui-focused fieldset': { borderColor: '#fb7102', }, }, }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedEmployee?.role ?? ""}
              onChange={e =>
                setSelectedEmployee({
                  ...selectedEmployee,
                  role: e.target.value,
                })
              }
              label="Role"
            >
              {[...new Set(employees.map(emp => emp.role))].map((role, idx) => (
                <MenuItem key={idx} value={role}>
                  {role || "No Role"}
                </MenuItem>
              ))}
            </Select>
          </FormControl >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="submit"
              sx={{
                background: "#fb7102",
                color: "white",
                width: "30%",
              }}
              onClick={() => {
                if (openDialog) {
                  // Update mode
                  if (rolePermissions?.is_update) {
                    handleUpdate(); // Your update handler
                  } else {
                    toast.error("You don't have permission to Update User.");
                  }
                } else {
                  // Add mode
                  if (rolePermissions?.is_add) {
                    handleAdd(); // Your add handler
                  } else {
                    toast.error("You don't have permission to Add User.");
                  }
                }
              }}
            >
              {openDialog ? "Update" : "Add"}
            </Button>
          </Box>

        </form>
      </DrawerComponent>

      {/* --- Delete Confirmation Dialog --- */}
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent style={{ fontFamily: 'Josefin Sans' }}>
          Are you sure you want to delete this employee? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            sx={{ backgroundColor: '#fb7102', color: "white" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{ backgroundColor: '#fb7102', color: "white" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}